<template>
  <div class="h-shr-edit">
    <div class="h-shr-edit__number">{{ orderNumber }}.</div>
    <button
      v-if="!room.is_cancelled && !room.update_restrictions?.delete"
      @click="onCardCancel"
      type="button"
      class="h-shr-edit__delete"
    >
      <img src="@/assets/img/clear-small-gray-icon.svg" alt="">
    </button>
    <div class="h-shr-edit__row">
      <div class="h-shr-edit__col">
        <div class="h-shr-edit__title">{{ room.room_category.name }}</div>
      </div>
      <div class="h-shr-edit__col">
        <app-cells :indent="false" position="end">
          <button v-if="room.is_cancelled" @click="onCardRestore" type="button" class="h-shr-edit__restore">
            Restore
            <img src="@/assets/img/hm/restore.svg" alt="">
          </button>
          <div
            class="h-shr-edit__status"
            :style="{
              backgroundColor: !room.id ? '#dee4d2' : is_changed && !room.is_cancelled ? '#fdc391' : room.is_cancelled ? '#f9e0d5' : '#afdcfd',
              color: !room.id ? '#468832' : is_changed && !room.is_cancelled ? '#a25e23' : room.is_cancelled ? '#e85c20' : '#3d7aa7'
            }"
          >
            {{ !room.id ? 'New' : is_changed && !room.is_cancelled ? 'Changed' : room.is_cancelled ? 'Deleted' : 'Original' }}
          </div>
        </app-cells>
      </div>
    </div>
    <div class="h-shr-edit__row">
      <div class="h-shr-edit__col">
        <div style="position: relative;">
          <app-date-picker
            :value="room.check_in"
            @input="emitUpdate('check_in', $event)"
            class="family"
            placeholder="Check-in"
            :clearable="false"
            :disabled="room.update_restrictions?.check_in"
            :disabled-date="disablePastDates"
            :default-value="room.check_in"
            :not-original="showOriginalCheckIn"
          />
          <img style="position: absolute; top: calc(50% - 5px); right: -16px;" src="@/assets/img/hm/arrow-right-grey.svg" alt="">
        </div>
        <div v-if="showOriginalCheckIn" class="h-shr-edit__desc">Original date: {{ originalCheckInLabel }}</div>
      </div>
      <div class="h-shr-edit__col">
        <app-date-picker
          :value="room.check_out"
          @input="emitUpdate('check_out', $event)"
          class="family"
          placeholder="Check-out"
          :clearable="false"
          :disabled="!room.check_in || room.update_restrictions?.check_out"
          :disabled-date="disableDatesFromCheckIn"
          :default-value="room.check_in"
          :not-original="showOriginalCheckOut"
          ref="check_out"
        />
        <div v-if="showOriginalCheckOut" class="h-shr-edit__desc">Original date: {{ originalCheckOutLabel }}</div>
      </div>
    </div>
    <div class="h-shr-edit__row">
      <div class="h-shr-edit__col">
        <HMTower horizontal>
          <template #title>Guests:</template>
          <template #body>
            <HMAgeAssignment :accomodation-type="room.accomodation_type" simple/>
          </template>
        </HMTower>
      </div>
      <div class="h-shr-edit__col">
        <app-cells :indent="false" gap="10px 33px">
          <HMTower horizontal>
            <template #title>Nights:</template>
            <template #body>
              <app-cells :indent="false" gap="10px">
                <HMChip>
                  {{ calcNumberOfNights || '-' }}
                  <template #icon>
                    <NightsIcon aria-hidden="true"/>
                  </template>
                </HMChip>
                <HMChip :color="mp.color" isMeal>
                  {{ mp.name }}
                </HMChip>
              </app-cells>
            </template>
          </HMTower>
          <HMTower horizontal>
            <template #title>QTY:</template>
            <template #body>
              <div style="position: relative;">
                <input
                  :value="room.count"
                  @input="onCountInput($event)"
                  type="number"
                  name="count"
                  class="h-shr-edit__input"
                  :class="{ 'h-shr-edit__input--not-original': showOriginalCount }"
                  :min="minCount"
                  :disabled="room.update_restrictions?.count"
                />
                <div v-if="showOriginalCount" class="h-shr-edit__desc h-shr-edit__desc--count">
                  {{ originalCount }}
                </div>
              </div>
            </template>
          </HMTower>
        </app-cells>
      </div>
    </div>
  </div>
</template>

<script>
import HMTower from '@/components/HMTower.vue'
import NightsIcon from '@/views/hotel-management/reservations/detail/icons/NightsIcon.vue'
import HMChip from '@/views/hotel-management/reservations/components/HMChip.vue'
import HMAgeAssignment from '@/components/HMAgeAssignment.vue'
import { addDays, format } from 'date-fns'
import isEqual from 'lodash.isequal'

export default {
  name: 'HotelShortRoomEdit',
  components: { HMAgeAssignment, HMChip, NightsIcon, HMTower },
  props: {
    room: { type: Object, required: true },
    orderNumber: { type: Number, default: 0 },
    mp: { type: Object, required: true },
  },
  data() {
    return {
      original_room: {},
    }
  },
  computed: {
    is_changed() {
      const room = this.room || {}
      const original = this.original_room || {}

      if (!room.id || room.id !== original.id) return false
      return !isEqual(
        { count: room.count, check_in: room.check_in, check_out: room.check_out },
        { count: original.count, check_in: original.check_in, check_out: original.check_out }
      )
    },
    minCount() {
      return this.room?.id ? (Number(this.room?.update_restrictions?.min_count) || 1) : 1
    },
    showOriginalCheckIn() {
      return !!(this.room.id && this.original_room.id &&
        this.room.check_in && this.original_room.check_in &&
        this.room.check_in !== this.original_room.check_in)
    },
    showOriginalCheckOut() {
      return !!(this.room.id && this.original_room.id && this.room.check_out && this.original_room.check_out && this.room.check_out !== this.original_room.check_out)
    },
    showOriginalCount() {
      return !!(this.room.id && this.original_room.id && this.room.count && this.original_room.count && this.room.count !== this.original_room.count)
    },
    originalCheckInLabel() {
      return this.original_room.check_in ? format(new Date(this.original_room.check_in), 'dd LLL yyyy') : ''
    },
    originalCheckOutLabel() {
      return this.original_room.check_out ? format(new Date(this.original_room.check_out), 'dd LLL yyyy') : ''
    },
    originalCount() {
      return this.original_room.count ? this.original_room.count : ''
    },
    calcNumberOfNights() {
      if (this.room.check_in && this.room.check_out) {
        const date1 = new Date(this.room.check_in)
        const date2 = new Date(this.room.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    },
  },
  created() {
    this.original_room = { ...this.room }
  },
  methods: {
    onCountInput(e) {
      const raw = e && e.target ? e.target.value : this.room.count
      let next = Math.floor(Number(raw) || 0)

      if (next < this.minCount) next = this.minCount
      this.emitUpdate('count', next)
      if (e && e.target) e.target.value = String(next)
    },
    emitUpdate(field, value) {
      this.$emit('update-field', { field, value })
    },
    onCardCancel() {
      if (this.room.id) this.$emit('card-cancel')
      else this.$emit('card-delete')
    },
    onCardRestore() {
      this.$emit('card-restore')
    },
    disablePastDates(date) {
      const d = this.normalizeDate(date)
      const today = this.normalizeDate(new Date())
      return d < today
    },
    disableDatesFromCheckIn(date) {
      const d = this.normalizeDate(date)
      const check_in = this.normalizeDate(this.room.check_in)
      const max = addDays(check_in, 10)

      return d <= check_in || d > max
    },
    normalizeDate(val) {
      const date = new Date(val)
      date.setHours(0, 0, 0, 0)
      return date
    },
  }
}
</script>

<style scoped>
.h-shr-edit {
  position: relative;
  margin-bottom: 10px;
  padding: 19px 30px 16px 58px;
  border-radius: 16px;
  border: 1px solid #e9e9e9;
  background-color: #fff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
}

.h-shr-edit:last-child {
  margin-bottom: 0;
}

.h-shr-edit__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.h-shr-edit__row:last-child {
  margin-bottom: 0;
}

.h-shr-edit__number {
  position: absolute;
  top: 23px;
  left: 24px;
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.h-shr-edit__delete {
  position: absolute;
  top: 2px;
  right: 2px;
  padding: 10px;
  line-height: 0;
}

.h-shr-edit__title {
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  font-weight: 500;
}

.h-shr-edit__desc {
  margin-top: 12px;
  color: #707070;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
}

.h-shr-edit__desc--count {
  margin-top: 0;
  position: absolute;
  top: 5px;
  right: -16px;
  text-decoration: line-through;
}

.h-shr-edit__input {
  width: 42px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e4e4e4;
  background-color: #fff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  text-align: center;
  padding: 0;
}

.h-shr-edit__input::-webkit-inner-spin-button,
.h-shr-edit__input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.h-shr-edit__input--not-original {
  border: 1px solid #ffcb9c;
}

.h-shr-edit__restore {
  width: 90px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  background-color: #dee4d2;
  color: #698262;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 500;
}

.h-shr-edit__restore img {
  display: inline-block;
  transform-origin: 50% 50%;
}

.h-shr-edit__restore:hover img {
  animation: hm-spin-ccw 0.9s linear infinite;
}

@keyframes hm-spin-ccw {
  to { transform: rotate(-360deg); }
}

.h-shr-edit__status {
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 14px;
  font-family: 'CoreSansCR', sans-serif;
  font-weight: 500;
  line-height: 18px;
}
</style>